/* ============================================================
   SAM-198 — create-flow taxonomy (step 1 "type" + step 2 "subtype"
   pickers), ported from Rodrigo's prototype (Confluence "12.4 Services
   admin UI — current vs proposed", `services-flow.js`: SERVICE_TYPES,
   SERVICE_SUBTYPES, UNIT_LABEL, usesUnits, schedulingType).

   SAM-198 follow-up (sam-lambda-functions PR #420,
   `feat/SAM-533-subtype-vocabulary-proposal`, `functions/booking/
   catalog_taxonomy.py`) widened the backend's closed `service.subtype`
   vocabulary from the original three (cabana/minconsumo/teetime) to all
   eleven of the prototype's subtypes, so every SUBTYPE_BACKEND_MAP entry
   below now sends the backend subtype key verbatim instead of falling back
   to a plain/custom service. `equipment` is the one subtype the backend
   accepts two scheduling types for (hourly/session -> 'flexible', full-day
   -> 'day') — this map carries the default ('flexible', matching both the
   prototype's own default and `SERVICE_SUBTYPE_SCHEDULING_TYPE.equipment`
   in useServices.ts); switching an equipment service to a full-day rental
   is still a manual Booking Type change after creation (no dedicated
   duration-type UI here, same as before).

   `SubtypeMapping.supported`/`.note` are kept (rather than deleted along
   with the old fallback mappings) so a *future* prototype subtype the
   backend hasn't caught up to yet can still degrade the same way — see
   CreateServiceFlow.tsx's `UnsupportedSubtypeNotice`, kept unused today for
   exactly that case — instead of every caller needing a new code path.
   ============================================================ */
import type { ServiceSubtype } from '../../../hooks/useServices';

export type CreateServiceType = 'appointment' | 'rental' | 'ticket';

export interface CreateTypeOption {
  k: CreateServiceType;
  n: string;
  d: string;
}

export interface CreateSubtypeOption {
  k: string;
  n: string;
  d: string;
  /** SAM-198 mock 2 v2 — what the setup presets, shown as the card's last line. */
  preset: string;
}

// SAM-198 mock 2 v2 (approved 2026-10-01): copy, order and preset lines per type.
export const CREATE_SERVICE_TYPES: CreateTypeOption[] = [
  { k: 'appointment', n: 'Appointments', d: 'Booked by time: professional services 1-on-1, classes, tee times.' },
  { k: 'rental', n: 'Rentals & Spaces', d: 'Stays, spaces and equipment, tables.' },
  { k: 'ticket', n: 'Access & Tickets', d: 'Day passes, cabanas, minimum consumption, events.' },
];

export const CREATE_SERVICE_SUBTYPES: Record<CreateServiceType, CreateSubtypeOption[]> = {
  appointment: [
    { k: 'private', n: 'Professional services (1-on-1)', d: 'A provider’s time: spa, trainer, lessons, consultations.', preset: 'Presets: time slots · per session' },
    { k: 'class', n: 'Classes (groups)', d: 'Fixed recurring schedule with limited places.', preset: 'Presets: fixed times · per guest' },
    { k: 'teetime', n: 'Tee Times', d: 'Golf groups off a starting tee.', preset: 'Presets: tee sheet · per player' },
  ],
  rental: [
    { k: 'stay', n: 'Stays / Multi-day', d: 'Villas, retreats, accommodation by night.', preset: 'Presets: date range · per unit' },
    { k: 'equipment', n: 'Spaces & Equipment', d: 'Courts, rooms, gear by the hour or day.', preset: 'Presets: time slots · per unit' },
    { k: 'tables', n: 'Tables / Seating', d: 'Restaurant tables by party size.', preset: 'Presets: time slots · free or deposit' },
  ],
  ticket: [
    { k: 'cabana', n: 'Cabana', d: 'One price per cabana with guests included. Extra guests priced per type.', preset: 'Presets: whole day · resources · price per cabana' },
    { k: 'daypass', n: 'Day Pass / Access', d: 'Entry for the day or a timed slot, priced per guest type.', preset: 'Presets: whole day · QR · Adult / Kid' },
    { k: 'minconsumo', n: 'Minimum Consumption', d: 'Entry redeemable against food and drink the same day.', preset: 'Presets: whole day · redeemable' },
    { k: 'single', n: 'Single-day Event', d: 'One date with a start time. Priced by event size.', preset: 'Presets: fixed · event size' },
    { k: 'multiday', n: 'Multi-day Event', d: 'Spans several days: festivals, conferences.', preset: 'Presets: day range · event size' },
  ],
};

/** The create-flow type a stored subtype belongs to (null for a custom service). */
export function createTypeOfSubtype(subtype: string): CreateServiceType | null {
  const hit = (Object.keys(CREATE_SERVICE_SUBTYPES) as CreateServiceType[])
    .find((t) => CREATE_SERVICE_SUBTYPES[t].some((o) => o.k === subtype));
  return hit ?? null;
}

/** Prototype `UNIT_LABEL` — subtypes backed by units the engine can only sell once at a time.
 * Matches catalog_taxonomy.py's `uses_units`/`unit_label` per subtype exactly. */
export const UNIT_LABEL: Record<string, string> = {
  tables: 'Tables',
  cabana: 'Cabanas',
  teetime: 'Starting tees',
  equipment: 'Units',
  stay: 'Units',
};

export function usesUnits(subtype: string): boolean {
  return !!UNIT_LABEL[subtype];
}

/** A default resource row seeded per subtype when it uses units — mirrors the prototype's `unit()` seeds. */
export const DEFAULT_UNIT_SEEDS: Record<string, Array<{ name: string; capacity: string }>> = {
  stay: [{ name: 'Unit 1', capacity: '2' }],
  equipment: [{ name: 'Unit 1', capacity: '1' }],
  teetime: [{ name: 'Tee 1', capacity: '4' }],
  cabana: [{ name: 'Cabana 1', capacity: '8' }],
  tables: [
    { name: 'Table 1', capacity: '2' },
    { name: 'Table 2', capacity: '4' },
    { name: 'Table 3', capacity: '6' },
  ],
};

export interface SubtypeMapping {
  /** '' only for a subtype the backend has no `service.subtype` counterpart for yet (supported === false). */
  backendSubtype: ServiceSubtype | '';
  bookingType: 'fixed' | 'day' | 'flexible';
  supported: boolean;
  /** Only set when `supported` is false — shown as a visible note in the create flow. */
  note?: string;
}

// SUBTYPE_BACKEND_MAP is keyed by the prototype's flat subtype code (unique across all three
// CREATE_SERVICE_TYPES groups), mirroring `subtypeName()` in services-flow.js. Every entry is the
// backend's real `service.subtype` value now (functions/booking/catalog_taxonomy.py, PR #420) —
// `backendSubtype` is always identical to the map's own key.
export const SUBTYPE_BACKEND_MAP: Record<string, SubtypeMapping> = {
  private: { backendSubtype: 'private', bookingType: 'fixed', supported: true },
  class: { backendSubtype: 'class', bookingType: 'fixed', supported: true },
  teetime: { backendSubtype: 'teetime', bookingType: 'fixed', supported: true },
  stay: { backendSubtype: 'stay', bookingType: 'day', supported: true },
  // Backend default for the two valid scheduling types on this subtype — see the module doc.
  equipment: { backendSubtype: 'equipment', bookingType: 'flexible', supported: true },
  tables: { backendSubtype: 'tables', bookingType: 'fixed', supported: true },
  daypass: { backendSubtype: 'daypass', bookingType: 'day', supported: true },
  cabana: { backendSubtype: 'cabana', bookingType: 'day', supported: true },
  minconsumo: { backendSubtype: 'minconsumo', bookingType: 'day', supported: true },
  single: { backendSubtype: 'single', bookingType: 'fixed', supported: true },
  multiday: { backendSubtype: 'multiday', bookingType: 'day', supported: true },
};

export function subtypeLabel(type: CreateServiceType, k: string): string {
  return CREATE_SERVICE_SUBTYPES[type]?.find((s) => s.k === k)?.n || k;
}

/** Prototype's `offerTypeLabel()` (services-flow.js) — a short singular noun for the "New …"
 * header, e.g. "New cabana" / "New tee time" / "New appointment". Deliberately NOT the same as
 * `subtypeLabel` above (the picker-card title, e.g. "Classes (Groups)" / "Spaces & Equipment"):
 * lowercasing that plural/parenthetical card label read oddly ("New classes (groups)"), so this
 * mirrors the prototype's own short-noun map with a type-label fallback instead. */
const NEW_SERVICE_NOUN: Record<string, string> = {
  daypass: 'day pass',
  cabana: 'cabana',
  minconsumo: 'minimum consumption',
  teetime: 'tee time',
  tables: 'table',
  stay: 'stay',
};

const TYPE_NOUN: Record<CreateServiceType, string> = {
  appointment: 'appointment',
  rental: 'rental',
  ticket: 'access',
};

export function newServiceNoun(type: CreateServiceType, subtype: string): string {
  if (NEW_SERVICE_NOUN[subtype]) return NEW_SERVICE_NOUN[subtype];
  if (subtype === 'single' || subtype === 'multiday') return 'event';
  return TYPE_NOUN[type] || 'service';
}
